import type { CodeCellLanguage } from '@/app/quadratic-core-types';
import type { UserFilesListType } from '@/dashboard/atoms/userFilesListFiltersAtom';
import type { ConnectionType } from 'quadratic-shared/typesAndSchemasConnections';

/** State param for CREATE_FILE when creating a file with code to insert and run (e.g. from AI flow). */
export type CreateFileStateParam = { language: CodeCellLanguage; codeString: string };

// Any routes referenced outside of the root router are stored here
export const ROUTES = {
  LOGOUT: '/logout',
  LOGIN: '/login',
  LOGIN_WITH_REDIRECT: (href?: string) =>
    `/login?${SEARCH_PARAMS.REDIRECT_TO.KEY}=${encodeURIComponent(href ?? window.location.pathname)}`,
  LOGIN_RESULT: '/login-result',
  SIGNUP: '/signup',
  SIGNUP_WITH_REDIRECT: (href?: string) =>
    `/login?type=signup&${SEARCH_PARAMS.REDIRECT_TO.KEY}=${encodeURIComponent(href ?? window.location.pathname)}`,
  VERIFY_EMAIL: '/verify-email',
  SEND_RESET_PASSWORD: '/send-reset-password',
  RESET_PASSWORD: '/reset-password',
  FILE: ({ uuid, searchParams }: { uuid: string; searchParams?: string }) =>
    `/file/${uuid}${searchParams ? `?${searchParams}` : ''}`,
  FILE_DUPLICATE: (uuid: string) => `/file/${uuid}/duplicate`,
  FILE_HISTORY: (uuid: string) => `/file/${uuid}/history`,
  EMBED: ({
    embedId,
    importUrl,
    uploadMode,
    readonly,
    sheets,
    clamp,
    initialView,
    preload,
    hideHeadings,
    hideGridLines,
    noFormatting,
    noClampToContent,
    noZoom,
  }: {
    embedId?: string;
    importUrl?: string;
    uploadMode?: 'postMessage';
    readonly?: boolean;
    sheets?: string[];
    clamp?: string;
    initialView?: string;
    preload?: ('python' | 'js')[];
    hideHeadings?: boolean;
    hideGridLines?: boolean;
    noFormatting?: boolean;
    noClampToContent?: boolean;
    noZoom?: boolean;
  }) => {
    const params = new URLSearchParams();
    if (embedId) params.set('embedId', embedId);
    if (importUrl) params.set('import', importUrl);
    if (uploadMode) params.set('uploadMode', uploadMode);
    if (readonly) params.set('readonly', '');
    if (sheets?.length) params.set('sheets', sheets.join(','));
    if (clamp?.trim()) params.set('clamp', clamp.trim());
    if (initialView?.trim()) params.set('initialView', initialView.trim());
    if (preload?.length) params.set('preload', preload.join(','));
    if (hideHeadings) params.set('hideHeadings', '');
    if (hideGridLines) params.set('hideGridLines', '');
    if (noFormatting) params.set('noFormatting', '');
    if (noClampToContent) params.set('noClampToContent', '');
    if (noZoom) params.set('noZoom', '');
    return `/embed?${params.toString()}`;
  },
  EMBED_CLAIM: (token: string) => `/embed/claim/${token}`,
  EMBED_SETTINGS: (embedId: string) => `/embed/settings?embedId=${embedId}`,
  FILES_CREATE: '/files/create',
  CREATE_FILE: (
    teamUuid: string,
    searchParams: {
      state?: CreateFileStateParam;
      prompt?: string | null;
      private?: boolean;
      chatId?: string | null;
      connectionUuid?: string | null;
      connectionType?: ConnectionType | null;
      connectionName?: string | null;
      folderUuid?: string | null;
    } = {}
  ) => {
    let url = new URL(window.location.origin + `/teams/${teamUuid}/files/create`);

    if (searchParams.state) {
      url.searchParams.set('state', btoa(JSON.stringify({ insertAndRunCodeInNewSheet: searchParams.state })));
    }
    if (searchParams.prompt) {
      url.searchParams.set('prompt', searchParams.prompt);
    }
    if (searchParams.private) {
      url.searchParams.set('private', 'true');
    }
    if (searchParams.chatId) {
      url.searchParams.set('chat-id', searchParams.chatId);
    }
    if (searchParams.connectionUuid) {
      url.searchParams.set('connection-uuid', searchParams.connectionUuid);
    }
    if (searchParams.connectionType) {
      url.searchParams.set('connection-type', searchParams.connectionType);
    }
    if (searchParams.connectionName) {
      url.searchParams.set('connection-name', searchParams.connectionName);
    }
    if (searchParams.folderUuid) {
      url.searchParams.set('folder-uuid', searchParams.folderUuid);
    }

    return url.pathname + url.search;
  },
  CREATE_FILE_FROM_TEMPLATE: ({
    teamUuid,
    publicFileUrlInProduction,
    additionalParams,
  }: {
    teamUuid: string;
    publicFileUrlInProduction: string;
    additionalParams: string;
  }) =>
    `/teams/${teamUuid}/files/create?template=${publicFileUrlInProduction}&private${
      additionalParams ? `&${additionalParams}` : ''
    }`,
  TEAMS: `/teams`,
  TEAM: (teamUuid: string) => `/teams/${teamUuid}`,
  TEAM_BILLING_MANAGE: (teamUuid: string) => `/teams/${teamUuid}/billing/manage`,
  TEAM_BILLING_SUBSCRIBE: (teamUuid: string, options?: { returnTo?: string; plan?: 'pro' | 'business' }) => {
    const base = `/teams/${teamUuid}/billing/subscribe`;
    const params = new URLSearchParams();
    if (options?.returnTo) {
      params.set('returnTo', options.returnTo);
    }
    if (options?.plan) {
      params.set('plan', options.plan);
    }
    const queryString = params.toString();
    return queryString ? `${base}?${queryString}` : base;
  },
  TEAM_CONNECTIONS: (teamUuid: string) => `/teams/${teamUuid}/connections`,
  TEAM_CONNECTIONS_NEW: (teamUuid: string) => `/teams/${teamUuid}/connections?view=new`,
  TEAM_CONNECTION_CREATE: (teamUuid: string, connectionType: ConnectionType) =>
    `/teams/${teamUuid}/connections?initial-connection-type=${connectionType}`,
  TEAM_CONNECTION: (teamUuid: string, connectionUuid: string, connectionType: ConnectionType) =>
    `/teams/${teamUuid}/connections?initial-connection-uuid=${connectionUuid}&initial-connection-type=${connectionType}`,
  TEAM_FILES: (teamUuid: string, { type }: { type?: UserFilesListType } = {}) =>
    `/teams/${teamUuid}/files${type ? `?type=${type}` : ''}`,
  TEAM_FILES_PRIVATE: (teamUuid: string) => ROUTES.TEAM_FILES(teamUuid, { type: 'private' }),
  TEAM_FILES_SHARED_WITH_ME: (teamUuid: string) => ROUTES.TEAM_FILES(teamUuid, { type: 'shared' }),
  TEAM_FILES_DELETED: (teamUuid: string) => `/teams/${teamUuid}/files/deleted`,
  TEAM_ONBOARDING: (teamUuid: string) => `/teams/${teamUuid}/onboarding`,
  TEAM_JOIN: (token: string) => `/teams/join/${token}`,
  TEAM_MEMBERS: (teamUuid: string) => `/teams/${teamUuid}/members`,
  TEAM_SETTINGS: (teamUuid: string) => `/teams/${teamUuid}/settings`,
  EDIT_TEAM: (teamUuid: string) => `/teams/${teamUuid}/edit`,
  ACTIVE_TEAM_SETTINGS: `/team/settings`,
  GET_STARTED: (persona: string) => `/get-started/${persona}`,
  TEAM_DRIVE: (teamUuid: string) => `/teams/${teamUuid}/drive`,
  TEAM_DRIVE_TEAM: (teamUuid: string) => `/teams/${teamUuid}/drive/team`,
  TEAM_DRIVE_PRIVATE: (teamUuid: string) => `/teams/${teamUuid}/drive/private`,
  TEAM_DRIVE_FOLDER: (teamUuid: string, folderUuid: string) => `/teams/${teamUuid}/drive/folders/${folderUuid}`,
  TEMPLATES: '/templates',
  LABS: '/labs',

  API: {
    FILE: (uuid: string) => `/api/files/${uuid}`,
    FILE_SHARING: (uuid: string) => `/api/files/${uuid}/sharing`,
    TEAM: (teamUuid: string) => `/api/teams/${teamUuid}`,
    CONNECTIONS: {
      POST: `/api/connections`,
      LIST: (teamUuid: string) => `/api/connections?team-uuid=${teamUuid}`,
      GET: ({ teamUuid, connectionUuid }: { teamUuid: string; connectionUuid: string }) =>
        `/api/connections?team-uuid=${teamUuid}&connection-uuid=${connectionUuid}`,
    },
  },

  IFRAME_INDEXEDDB: '/iframe-indexeddb',
} as const;

export const ROUTE_LOADER_IDS = {
  ROOT: 'root',
  FILE: 'file',
  EMBED: 'embed',
  DASHBOARD: 'dashboard',
} as const;

export const SEARCH_PARAMS = {
  SHEET: { KEY: 'sheet' },
  DIALOG: { KEY: 'dialog', VALUES: { EDUCATION: 'education' } },
  SNACKBAR_MSG: { KEY: 'snackbar-msg' }, // VALUE can be any message you want to display
  SNACKBAR_SEVERITY: { KEY: 'snackbar-severity', VALUE: { ERROR: 'error' } },
  // Used to load a specific checkpoint (version history), e.g. /file/123?sequence_num=456
  SEQUENCE_NUM: { KEY: 'sequence_num' },
  LOGIN_TYPE: { KEY: 'type', VALUES: { SIGNUP: 'signup' } },
  REDIRECT_TO: { KEY: 'redirectTo' },
  // Used to open the scheduled tasks panel when the file loads
  SCHEDULED_TASKS: { KEY: 'scheduled-tasks' },
  PERSONA: { KEY: 'persona' },
} as const;

export const VALID_PERSONAS = ['accountant', 'marketer', 'financial-analyst'] as const;
export type Persona = (typeof VALID_PERSONAS)[number];
